import { useEffect, useState, useCallback, useRef } from 'react';
import { useClientId } from "../context/context.js";
import * as helpers from "../utils/utils.js";

export function useSelections() {

  const yesterday = new Date().setUTCHours(0, 0, 0, 0);

  const [selectedSlots, setSelectedSlots] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [visibleSlots, setVisibleSlots] = useState([]);
  const [visibleUnit, setVisibleUnit] = useState(null); // null by default means all units are visible
  const [defaultStartDate, setDefaultStartDate] = useState();
  const [slotsLoading, setSlotsLoading] = useState(false);
  const calledStartDatesRef = useRef(new Set());

  const cliendId = useClientId();

  // fetches availability for a given range
  const fetchAvailability = useCallback(async ({firstDay, lastDay, status, getPast=false}) => {
    setSlotsLoading(true);

    if (calledStartDatesRef.current.has(firstDay)) {
      setSlotsLoading(false);
      return;
    }

    calledStartDatesRef.current.add(firstDay);
    const response = await helpers.get(`/clients/${cliendId}/date-slots?startDate=${firstDay}&endDate=${lastDay}&${status && 'status=' + status}`);
    if (response.success) {
        setAvailableSlots((prevSlots) => {
            const updatedSlots = [...prevSlots];
            
            response.data.forEach(resultSlot => {
              if (
                !updatedSlots.some(slot => slot.id === resultSlot.id) &&
                (getPast || resultSlot.date >= yesterday.valueOf())
              ) {
                updatedSlots.push(resultSlot);
              }
            });
            
            
            return updatedSlots;
        });
    }


    setSlotsLoading(false);
    return;
  }, [cliendId, yesterday]);

  const fetchFirstSlot = useCallback(async (status) => {
    const response = await helpers.get(`/clients/${cliendId}/date-slots/first-available?status=${status}`);
    
    if (response.success) {
      const firstDate = response.data.date;
      return firstDate;
    }

    return;
  }, [cliendId]);

  const fetchDateSlots = useCallback(async (date, status) => {
    const response = await helpers.get(`/clients/${cliendId}/date-slots/?startDate=${date}&endDate=${date+86400000}&status=${status}`);
    
    if (response.success) {
      const dateSlots = response.data;
      return dateSlots;
    }

    return;
  }, [cliendId]);

  // checks if the selected slots are still available
  const checkSelectionAvailability = async (slots) => {
    const unavailableSlots = [];
    const availableSlots = [];

    for (const slot of slots) {
      const response = await helpers.get(`/clients/${cliendId}/date-slots/${slot.id}`);
      if (!response.success) {
        console.error("Error fetching slot availability:", response.error);
        continue; // Skip this slot if there's an error
      }
      if (!response.success || response.data.status === "unavailable" || response.data.status === "booked") {
        // If the slot is unavailable or booked, add it to the unavailable slots
        unavailableSlots.push(slot);
      } else {
        availableSlots.push(slot);
      }
    }

    return { unavailableSlots, availableSlots };
  }


  const clearSlots = () => {
    setSelectedSlots([]);
  };

  const removeSlot = ({ slotId, date, unitId }) => {
    setSelectedSlots((prevSlots) =>
      prevSlots.filter((slot) => {
        if (slotId) return slot.id !== slotId;
        if (date && unitId) return !(slot.date === date && slot.unitId === unitId);
        return true;
      })
    );
  };
  

  const addSlot = (slot) => {
    setSelectedSlots((prevSlots) => [...prevSlots, slot]);
  };

  const clearDates = () => {
    setSelectedDates([]);
    setSelectedSlots([]);
  };

  const removeDate = (date) => {
    setSelectedSlots((prevSlots) => prevSlots.filter((slot) => slot.date !== date));
    setSelectedDates((prevDates) => prevDates.filter((d) => d !== date));
  };

  const addDate = (date) => {
    setSelectedDates((prevDates) => {
      const updatedDates = [...prevDates, date];
      updatedDates.sort();
      return updatedDates;
    });
  };

  return {
    addSlot,
    removeSlot,
    clearSlots,
    addDate,
    removeDate,
    clearDates,
    selectedDates,
    selectedSlots,
    visibleSlots,
    setVisibleUnit,
    availableSlots,
    visibleUnit,
    defaultStartDate,
    setDefaultStartDate,
    slotsLoading,
    fetchAvailability,
    fetchFirstSlot,
    fetchDateSlots,
  };
}
